import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { pushSettings } from '@bb/contracts';
import { Badge, Button, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, RadioGroup, Select } from '@bb/ui';
import { ApiError, type AppInstanceSummary, type IosCategoryBody, type IosCategoryButton, type IosCategoryView } from '../../../api/client.ts';
import { contractIssues, problemFieldErrors, problemText } from './problems.ts';
import { pushKeys, useApi, type PushSettingsPageData } from './usePushSettingsData.ts';

type Preset = keyof typeof pushSettings.IOS_CATEGORY_PRESETS;
type ButtonLike = { title: string; opensApp?: boolean; destructive?: boolean; authRequired?: boolean };

export const MAX_CATEGORY_BUTTONS = 4;
const PRESETS = Object.keys(pushSettings.IOS_CATEGORY_PRESETS) as Preset[];
const PRESET_LABEL: Record<Preset, string> = { accept_decline: 'Accept / Decline', yes_no: 'Yes / No', confirm_cancel: 'Confirm / Cancel', more: 'More' };
const MESSAGES: Record<string, string> = {
  categoryId: 'Letters, digits, _ . - (up to 64).',
  buttons: 'Choose a preset or add at least one button.',
};

/** «Close position (opens the app, destructive, requires unlock), Snooze». */
export function describeButtons(buttons: readonly ButtonLike[]): string {
  return buttons
    .map((b) => {
      const flags = [b.opensApp !== false && 'opens the app', b.destructive && 'destructive', b.authRequired && 'requires unlock'].filter(Boolean);
      return flags.length ? `${b.title} (${flags.join(', ')})` : b.title;
    })
    .join(', ');
}

const buttonsOf = (c: Pick<IosCategoryView, 'preset' | 'buttons'>): readonly ButtonLike[] => (c.preset ? pushSettings.IOS_CATEGORY_PRESETS[c.preset] : c.buttons);

interface Draft {
  mode: 'preset' | 'custom';
  preset: Preset;
  categoryId: string;
  appInstanceId: string;
  buttons: IosCategoryButton[];
}

const newDraft = (): Draft => ({ mode: 'preset', preset: 'accept_decline', categoryId: '', appInstanceId: '', buttons: [] });
const nextButtonId = (buttons: IosCategoryButton[]) => {
  for (let n = 1; ; n++) if (!buttons.some((b) => b.id === `button_${n}`)) return `button_${n}`;
};

/** The body to send or one message per field (`categoryId`, `buttons`, `buttons.<i>.<field>`). */
function checkCategory(d: Draft): { body: IosCategoryBody | null; issues: Record<string, string> } {
  const candidate =
    d.mode === 'preset'
      ? { appInstanceId: null, categoryId: pushSettings.IOS_CATEGORY_PRESET_IDS[d.preset], preset: d.preset, buttons: [] }
      : { appInstanceId: null, categoryId: d.categoryId.trim(), preset: null, buttons: d.buttons };
  const parsed = pushSettings.IosCategoryBody.safeParse(candidate);
  if (!parsed.success) {
    const issues = contractIssues(parsed.error.issues, MESSAGES);
    for (const [key, message] of Object.entries(issues)) {
      if (/^buttons\.\d+\.id$/.test(key)) issues[key] = 'Lowercase letters, digits and _ (up to 32).';
      else if (/^buttons\.\d+\.title$/.test(key)) issues[key] = 'Enter a title of up to 40 characters.';
      else if (key === 'buttons' && message.toLowerCase().includes('unique')) issues[key] = 'Button IDs must be unique.';
      else if (key === 'buttons' && candidate.buttons.length > MAX_CATEGORY_BUTTONS) issues[key] = `Up to ${MAX_CATEGORY_BUTTONS} buttons.`;
    }
    return { body: null, issues };
  }
  return { body: { ...parsed.data, appInstanceId: d.appInstanceId || null }, issues: {} };
}

/**
 * iOS notification categories (BB-77): the action buttons an app registers (`UNNotificationCategory`), from a preset
 * with a stable id (`bb_yes_no`…) or up to four custom buttons. A push names the category; the app draws its buttons.
 * Categories are archived, not deleted.
 */
export function IosCategoriesEditor({ environmentId, data, canEdit }: { environmentId: string; data: PushSettingsPageData; canEdit: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Draft | null>(null);
  const [archiving, setArchiving] = useState<IosCategoryView | null>(null);
  const refresh = async () => {
    void queryClient.invalidateQueries({ queryKey: pushKeys.sdkConfigs(environmentId) });
    await queryClient.invalidateQueries({ queryKey: pushKeys.categories(environmentId) });
  };
  const create = useMutation({
    mutationFn: (body: IosCategoryBody) => api.createPushIosCategory(environmentId, body),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.archivePushIosCategory(environmentId, id),
    onSuccess: async () => {
      setArchiving(null);
      await refresh();
    },
  });
  const iosApps = data.appsWithPush.filter((a) => a.platform === 'ios');
  const appName = (id: string | null) => (id ? (data.apps.data?.find((a) => a.id === id)?.name ?? 'An app that no longer exists') : 'All iOS apps');

  if (data.categories.isError) return <ErrorState title="iOS categories could not be loaded" message={problemText(data.categories.error)} onRetry={() => void data.categories.refetch()} />;
  if (!data.categories.data) return <p aria-busy="true">Loading categories…</p>;
  const rows = [...data.categories.data].sort((a, b) => Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt)));

  return (
    <>
      <p className="hint">A push with buttons names one of these categories; the app registers them at startup (see SDK configuration) and iOS draws the buttons.</p>
      {canEdit && (
        <div className="actions">
          <Button
            variant="primary"
            onClick={() => {
              create.reset();
              setEditing(newDraft());
            }}
          >
            Add category
          </Button>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title="No iOS categories" description="iOS pushes go without action buttons. Add a preset such as Yes / No or your own buttons." />
      ) : (
        <div className="push-table-scroll">
          <table className="table" aria-label="iOS categories">
            <thead>
              <tr>
                <th scope="col">Category ID</th>
                <th scope="col">Buttons</th>
                <th scope="col">Apps</th>
                <th scope="col">Status</th>
                {canEdit && (
                  <th scope="col">
                    <span className="bb-visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className={c.archivedAt ? 'push-row--archived' : undefined}>
                  <td>
                    <code>{c.categoryId}</code>
                  </td>
                  <td>
                    {describeButtons(buttonsOf(c))} {c.preset && <Badge tone="neutral">Preset</Badge>}
                  </td>
                  <td>{appName(c.appInstanceId)}</td>
                  <td>{c.archivedAt ? 'Archived' : 'Active'}</td>
                  {canEdit && (
                    <td>
                      {!c.archivedAt && (
                        <Button variant="ghost" size="sm" aria-label={`Archive ${c.categoryId}`} onClick={() => setArchiving(c)}>
                          Archive
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <CategoryDialog
          draft={editing}
          iosApps={iosApps}
          busy={create.isPending}
          error={create.error}
          onChange={(next) => {
            create.reset();
            setEditing(next);
          }}
          onCancel={() => setEditing(null)}
          onSave={(body) => create.mutate(body)}
        />
      )}
      <ConfirmDialog open={archiving !== null} onOpenChange={(o) => !o && setArchiving(null)} title={`Archive ${archiving?.categoryId ?? ''}?`} confirmLabel="Archive" busy={archive.isPending} onConfirm={() => archiving && archive.mutate(archiving.id)}>
        It leaves the registry and the generated config. Pushes that still name it arrive without buttons on apps that no longer register it.
        {archive.isError && <span role="alert"> {problemText(archive.error)}</span>}
      </ConfirmDialog>
    </>
  );
}

function CategoryDialog({ draft, iosApps, busy, error, onChange, onCancel, onSave }: {
  draft: Draft;
  iosApps: AppInstanceSummary[];
  busy: boolean;
  error: unknown;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: (body: IosCategoryBody) => void;
}) {
  const checked = checkCategory(draft);
  const server = error instanceof ApiError && error.status === 409 ? { categoryId: 'This category ID is already in the registry for these apps.' } : error instanceof ApiError && error.code === 'push_platform_mismatch' ? { appInstanceId: 'Choose an iOS app.' } : problemFieldErrors(error);
  const issue = (key: string) => checked.issues[key] ?? server[key];
  const unmapped = error != null && Object.keys(server).length === 0 ? problemText(error) : null;
  const setButton = (index: number, patch: Partial<IosCategoryButton>) => onChange({ ...draft, buttons: draft.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)) });
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onCancel()}
      title="Add iOS category"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!checked.body} onClick={() => checked.body && onSave(checked.body)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack-form push-category">
        <RadioGroup
          label="Buttons"
          options={[
            { value: 'preset', label: 'From a preset' },
            { value: 'custom', label: 'Custom buttons' },
          ]}
          value={draft.mode}
          onValueChange={(mode) => onChange({ ...draft, mode: mode as Draft['mode'] })}
        />
        {draft.mode === 'preset' ? (
          <>
            <Field label="Preset" error={issue('categoryId')}>
              <Select options={PRESETS.map((p) => ({ value: p, label: PRESET_LABEL[p] }))} value={draft.preset} onChange={(e) => onChange({ ...draft, preset: e.target.value as Preset })} />
            </Field>
            <dl className="details">
              <dt>Category ID</dt>
              <dd>
                <code>{pushSettings.IOS_CATEGORY_PRESET_IDS[draft.preset]}</code>
              </dd>
              <dt>Buttons</dt>
              <dd>{describeButtons(pushSettings.IOS_CATEGORY_PRESETS[draft.preset])}</dd>
            </dl>
          </>
        ) : (
          <>
            <Field label="Category ID" hint="Exactly as the app registers it." error={issue('categoryId')}>
              <Input value={draft.categoryId} spellCheck={false} placeholder="trade_actions" onChange={(e) => onChange({ ...draft, categoryId: e.target.value })} />
            </Field>
            {draft.buttons.map((b, i) => (
              <fieldset key={i} className="push-category__button" aria-label={`Button ${i + 1}`}>
                <legend>Button {i + 1}</legend>
                <Field label="Button ID" hint="The action id the push and the app's handler use." error={issue(`buttons.${i}.id`)}>
                  <Input value={b.id} spellCheck={false} onChange={(e) => setButton(i, { id: e.target.value })} />
                </Field>
                <Field label="Title" error={issue(`buttons.${i}.title`)}>
                  <Input value={b.title} onChange={(e) => setButton(i, { title: e.target.value })} />
                </Field>
                <Checkbox label="Opens the app" checked={b.opensApp} onCheckedChange={(opensApp) => setButton(i, { opensApp })} />
                <Checkbox label="Destructive" checked={b.destructive} onCheckedChange={(destructive) => setButton(i, { destructive })} />
                <Checkbox label="Requires unlock" checked={b.authRequired} onCheckedChange={(authRequired) => setButton(i, { authRequired })} />
                <Button variant="ghost" size="sm" onClick={() => onChange({ ...draft, buttons: draft.buttons.filter((_, j) => j !== i) })}>
                  Remove button
                </Button>
              </fieldset>
            ))}
            <div className="actions">
              <Button
                size="sm"
                disabled={draft.buttons.length >= MAX_CATEGORY_BUTTONS}
                onClick={() => onChange({ ...draft, buttons: [...draft.buttons, { id: nextButtonId(draft.buttons), title: '', opensApp: true, destructive: false, authRequired: false }] })}
              >
                Add button
              </Button>
              <span className="hint">Up to {MAX_CATEGORY_BUTTONS} buttons.</span>
            </div>
            {issue('buttons') && (
              <p className="bb-field__error" role="alert">
                {issue('buttons')}
              </p>
            )}
          </>
        )}
        <Field label="Apps" error={issue('appInstanceId')}>
          <Select options={[{ value: '', label: 'All iOS apps' }, ...iosApps.map((a) => ({ value: a.id, label: a.name }))]} value={draft.appInstanceId} onChange={(e) => onChange({ ...draft, appInstanceId: e.target.value })} />
        </Field>
        {unmapped && <p role="alert">{unmapped}</p>}
      </div>
    </Modal>
  );
}
